import {
  Component,
  computed,
  effect,
  inject,
  ElementRef,
  OnDestroy,
  signal,
  viewChild,
  afterNextRender,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OpenSearchDrawerService } from '../../../../services/open-search-drawer.service';
import { OpenSearchService } from '../../../../services/open-search.service';
import { CopyDataDirective } from '../../../../directives/copy-data.directive';
import {
  CuratedLesson,
  CuratedLessonSource,
  SourceResponse,
} from '../../../../models/response-open-search';

@Component({
  selector: 'app-sources-drawer',
  imports: [
    CommonModule,
    MatIconModule,
    MatSelectModule,
    MatDividerModule,
    TranslateModule,
    CopyDataDirective,
  ],
  templateUrl: './sources-drawer.component.html',
  styleUrls: ['./sources-drawer.component.scss'],
  host: {
    '(document:mousemove)': 'onMouseMove($event)',
    '(document:mouseup)': 'onMouseUp()',
    '(window:resize)': 'adjustPanelOnWindowResize()',
  },
})
export class SourcesDrawerComponent implements OnDestroy {
  svc = inject(OpenSearchDrawerService);
  openSearchService = inject(OpenSearchService);
  private translateService = inject(TranslateService);
  private el = inject(ElementRef);
  private panelElement = viewChild<ElementRef<HTMLElement>>('sourcesPanel');
  private resizing = signal(false);
  private startX = signal(0);
  private startWidth = signal(0);
  private screenWidth = signal(window.screen.width);
  private screenHeight = signal(window.screen.height);
  private readonly ABSOLUTE_MIN_WIDTH = 350;
  private readonly ABSOLUTE_MAX_WIDTH = 1400;
  private readonly MIN_CONTENT_PERCENTAGE = 0.3;
  spinnerConversational = computed(() => this.openSearchService.spinnerConversational());

  activeInteraction = computed(() => {
    const interactions = this.openSearchService.historyResponse()?.openQueryInteractions ?? [];
    return (
      interactions.find(x => x.isActive) ??
      [...interactions].reverse().find(x => x.response !== null)
    );
  });

  answer = computed(() => this.activeInteraction()?.question);

  sources = computed(() => this.activeInteraction()?.sources);

  curatedLessonSources = computed<CuratedLessonSource[]>(() => {
    const interaction = this.activeInteraction();
    if (interaction?.modeType?.toLowerCase() !== 'lessons') return [];
    const lessons = interaction.modeResponse?.lessons ?? [];
    let reference = 0;
    return lessons.flatMap(lesson =>
      this.normalizeLessonSources(lesson).map(source => ({ ...source, reference: ++reference })),
    );
  });

  sourcesString = computed(() => {
    const sources = this.sources();
    const normalSources = sources
      ? sources.map(source => `${source.description}: ${source.url}`)
      : [];
    const lessonSources = this.curatedLessonSources().map(
      source => `${source.fileName}: ${source.url}`,
    );
    const sections: string[] = [];
    if (normalSources.length) sections.push(normalSources.join('\n'));
    if (lessonSources.length) {
      sections.push(
        `${this.translateService.instant('OPEN_SEARCH.CURATED_LESSON_SOURCES')}\n${lessonSources.join('\n')}`,
      );
    }
    return sections.join('\n\n');
  });

  private normalizeLessonSources(lesson: CuratedLesson): Omit<CuratedLessonSource, 'reference'>[] {
    const toArray = (value: unknown): string[] =>
      Array.isArray(value)
        ? value.filter((item): item is string => typeof item === 'string')
        : typeof value === 'string' && value.trim()
          ? [value]
          : [];
    const urls = toArray(lesson.url);
    const fileNames = toArray(lesson.file_name ?? lesson.fileName);
    const nested = Array.isArray(lesson.sources) ? lesson.sources : [];
    const nestedSources = nested.flatMap(source => {
      const value = source as SourceResponse & { fileName?: string; filename?: string };
      return value.url
        ? [
            {
              url: value.url,
              fileName: value.fileName ?? value.filename ?? value.description ?? value.url,
            },
          ]
        : [];
    });
    return [
      ...urls.map((url, index) => ({ url, fileName: fileNames[index] ?? url })),
      ...nestedSources,
    ].filter(source => /^https?:\/\//i.test(source.url));
  }
  private maxAllowedWidth = computed(() => {
    const viewportWidth = window.innerWidth;
    const maxPercentage = 1 - this.MIN_CONTENT_PERCENTAGE;
    const maxFromViewport = Math.floor(viewportWidth * maxPercentage);
    return Math.min(maxFromViewport, this.ABSOLUTE_MAX_WIDTH);
  });
  private minWidth = computed(() => {
    const viewportWidth = window.innerWidth;

    if (viewportWidth >= 1921) {
      return 700;
    } else if (viewportWidth < 480) {
      return Math.max(240, Math.floor(viewportWidth * 0.7));
    }
    return this.ABSOLUTE_MIN_WIDTH;
  });

  constructor() {
    afterNextRender(() => {
      this.adjustPanelOnWindowResize();
      document.body.style.overflowX = 'hidden';
    });
    effect(() => {
      const activeUrl = this.openSearchService.activeTooltipUrl()?.url;
      const sourceItems = document.querySelectorAll('.list-source li');
      sourceItems.forEach(li => {
        const link = li.querySelector('a');
        if (!link) {
          li.classList.remove('active', 'tooltip-no-wrap');
          return;
        }
        const isValidActiveUrl = !!activeUrl && /^https?:\/\//i.test(activeUrl);
        const itemHref = link.getAttribute('href') ?? '';
        const isValidItemHref = /^https?:\/\//i.test(itemHref);
        const isActive = isValidActiveUrl && isValidItemHref && itemHref === activeUrl;
        li.classList.toggle('active', isActive);
        li.classList.toggle('tooltip-no-wrap', isActive);
      });
    });
    effect(() => {
      const panel = this.panelElement();
      if (panel) {
        this.adjustPanelToScreen(panel.nativeElement);
      }
    });
  }

  ngOnDestroy() {
    this.cleanup();
    document.body.style.overflowX = '';
  }

  private cleanup() {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }

  private getPanel(): HTMLElement | null {
    const panelRef = this.panelElement();
    if (panelRef) {
      return panelRef.nativeElement;
    }
    return this.el.nativeElement.querySelector('.sources-panel') as HTMLElement;
  }

  adjustPanelOnWindowResize() {
    const panel = this.getPanel();
    if (!panel) return;

    const currentScreenWidth = window.screen.width;
    const currentScreenHeight = window.screen.height;

    const hasScreenChanged =
      this.screenWidth() !== currentScreenWidth || this.screenHeight() !== currentScreenHeight;

    if (hasScreenChanged) {
      this.screenWidth.set(currentScreenWidth);
      this.screenHeight.set(currentScreenHeight);
    }

    this.adjustPanelToScreen(panel);
  }

  private adjustPanelToScreen(panel: HTMLElement) {
    const currentWidth = panel.getBoundingClientRect().width;
    const maxAllowed = this.maxAllowedWidth();
    const minAllowed = this.minWidth();
    const viewportWidth = window.innerWidth;

    let newWidth = currentWidth;

    if (currentWidth > maxAllowed) {
      newWidth = maxAllowed;
    } else if (currentWidth < minAllowed) {
      newWidth = minAllowed;
    } else if (!panel.style.width || currentWidth === 0) {
      const optimalWidth = Math.floor(viewportWidth * 0.35);
      newWidth = Math.max(minAllowed, Math.min(optimalWidth, maxAllowed));
    }

    panel.style.width = `${newWidth}px`;
    this.svc.rightWidth.set(newWidth);
  }

  onStartResize(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();

    this.resizing.set(true);
    this.startX.set(ev.clientX);

    const panel = this.getPanel();
    if (!panel) return;

    this.startWidth.set(panel.getBoundingClientRect().width);

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.body.style.overflowX = 'hidden';
  }

  onMouseMove(ev: MouseEvent) {
    if (!this.resizing()) return;

    const panel = this.getPanel();
    if (!panel) return;

    ev.preventDefault();

    const deltaX = this.startX() - ev.clientX;
    let newWidth = this.startWidth() + deltaX;

    const maxWidth = this.maxAllowedWidth();
    const minWidthValue = this.minWidth();

    newWidth = Math.max(minWidthValue, Math.min(maxWidth, newWidth));
    panel.style.width = `${newWidth}px`;
    this.svc.rightWidth.set(newWidth);
    requestAnimationFrame(() => {
      const hasHorizontalScroll = document.documentElement.scrollWidth > window.innerWidth;
      if (hasHorizontalScroll && panel.style.width) {
        const currentPanelWidth = panel.getBoundingClientRect().width;
        const overflow = document.documentElement.scrollWidth - window.innerWidth;
        const correctedWidth = currentPanelWidth - overflow - 10;
        panel.style.width = `${Math.max(minWidthValue, correctedWidth)}px`;
        this.svc.rightWidth.set(Math.max(minWidthValue, correctedWidth));
      }
    });
  }

  onMouseUp() {
    if (!this.resizing()) return;

    this.resizing.set(false);

    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.body.style.overflowX = 'hidden';
  }

  onEsc() {
    this.svc.close();
  }
}
